import React, { useEffect, useMemo, useState } from "react";

import {
  Field,
  columnRank,
  fieldLabel,
  isCreateDrawerPrimary,
  isPrimaryKey,
  isSecretField,
} from "../lib/schema-field";
import { DataCell } from "./DataCell";
import { Modal } from "./Modal";
import { RowHistory } from "./RowHistory";
import { Button } from "./ui/Button";

interface RowDetailModalProps {
  open: boolean;
  onClose: () => void;
  row: any | null;
  schema: Field[];
  title?: string;
  /** Rendered in the footer, e.g. an "Edit" button the grid supplies. */
  actions?: React.ReactNode;
  /**
   * Turns the History tab on (MOSA-368 slice 1). All three are needed to ask
   * the API for this row's history, so the tab is hidden without them: an
   * Overview card has no row identity to ask about.
   */
  datasourceId?: string;
  collection?: string;
  rowKey?: string | null;
}

type Tab = "details" | "history";

type Entry = { key: string; field?: Field };

// Grid ids the app attaches to rows that have no primary key of their own.
// They are plumbing, not data, so they stay out of the detail list.
const SYNTHETIC_KEYS = ["_syntheticId", "_ctid", "_rowNumber"];
const RELATED_CAP = 8;

const isHiddenKey = (key: string): boolean =>
  SYNTHETIC_KEYS.includes(key) || key.startsWith("__") || isSecretField(key);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;

const visibleKeys = (record: Record<string, unknown>): string[] =>
  Object.keys(record).filter((key) => !isHiddenKey(key));

/** One extra nesting level for a related row; deeper objects go through DataCell. */
const NestedRecord: React.FC<{ record: Record<string, unknown> }> = ({
  record,
}) => {
  const keys = visibleKeys(record);
  if (keys.length === 0) return <DataCell value={record} wrap />;
  return (
    <dl className="m-0 grid grid-cols-1 gap-x-3 rounded-md border border-od-border bg-od-surface-2 px-2 sm:grid-cols-[minmax(6rem,9rem)_1fr]">
      {keys.map((key) => (
        <React.Fragment key={key}>
          <dt className="border-b border-od-border py-1.5 text-xs font-medium text-od-muted">
            {key}
          </dt>
          <dd className="m-0 min-w-0 break-words border-b border-od-border py-1.5 text-xs text-od-text">
            <DataCell value={record[key]} wrap />
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

const RelatedValue: React.FC<{ value: unknown; field?: Field }> = ({
  value,
  field,
}) => {
  if (isPlainObject(value)) {
    return <NestedRecord record={value} />;
  }

  if (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(isPlainObject)
  ) {
    const shown = value.slice(0, RELATED_CAP);
    const rest = value.length - shown.length;
    return (
      <div className="flex flex-col gap-2">
        <div className="text-xs font-medium text-od-muted">
          {value.length} related
        </div>
        {shown.map((item, index) => (
          <NestedRecord key={index} record={item} />
        ))}
        {rest > 0 && (
          <div className="text-xs text-od-muted">+{rest} more</div>
        )}
      </div>
    );
  }

  return <DataCell value={value} field={field} wrap />;
};

const rowSubtitle = (row: any): string | undefined => {
  if (!row) return undefined;
  for (const key of ["name", "title", "label", "slug"] as const) {
    const candidate = row[key];
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  if (row.id != null && row.id !== "") return `#${row.id}`;
  return undefined;
};

const FieldRow: React.FC<{
  entry: Entry;
  row: any;
}> = ({ entry, row }) => {
  const { key, field } = entry;
  return (
    <div className="border-b border-od-border py-2 sm:contents">
      <dt className="text-xs font-medium text-od-muted sm:border-b sm:border-od-border sm:py-2">
        {field ? fieldLabel(field) : key}
        {field && isPrimaryKey(field) && (
          <span className="ml-1.5 text-xs uppercase tracking-wide text-od-muted opacity-60">
            key
          </span>
        )}
      </dt>
      <dd className="m-0 min-w-0 break-words pt-0.5 text-xs text-od-text sm:border-b sm:border-od-border sm:py-2">
        <RelatedValue value={row?.[key]} field={field} />
      </dd>
    </div>
  );
};

/**
 * Read-only counterpart to the edit form. The grid truncates every cell to fit
 * a column, so a long description or a JSON blob is unreadable in place; this
 * shows the whole row at full width without putting it in an editable state.
 *
 * Secrets never appear here (same rule as the grid and relation picker). Audit
 * timestamps, primary keys and JSON sit behind the extras disclosure so the
 * first screen matches the create drawer: what the row is, not its plumbing.
 */
export const RowDetailModal: React.FC<RowDetailModalProps> = ({
  open,
  onClose,
  row,
  schema,
  title = "Record",
  actions,
  datasourceId,
  collection,
  rowKey,
}) => {
  const [showExtras, setShowExtras] = useState(false);
  const [tab, setTab] = useState<Tab>("details");
  const tabId = React.useId();
  const tabRefs = React.useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({});

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, value: Tab) => {
    let next: Tab;
    if (event.key === "Home") next = "details";
    else if (event.key === "End") next = "history";
    else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      next = value === "details" ? "history" : "details";
    } else return;
    event.preventDefault();
    setTab(next);
    tabRefs.current[next]?.focus();
  };

  useEffect(() => {
    setShowExtras(false);
    setTab("details");
  }, [row]);

  const hasHistory = Boolean(datasourceId && collection && rowKey);

  const { primary, extras } = useMemo(() => {
    if (!row) return { primary: [] as Entry[], extras: [] as Entry[] };
    const known = new Set(schema.map((field) => field.name));
    const named = schema
      .filter((field) => !isSecretField(field))
      .map((field, index) => ({ key: field.name, field, index }))
      .sort((a, b) => {
        const ra = columnRank(a.field);
        const rb = columnRank(b.field);
        return ra === rb ? a.index - b.index : ra - rb;
      })
      .map(({ key, field }) => ({ key, field }));
    const related = Object.keys(row)
      .filter((key) => !known.has(key) && !isHiddenKey(key))
      .map((key) => ({ key, field: undefined as Field | undefined }));

    const lead = named.filter(({ field }) => isCreateDrawerPrimary(field));
    const rest = named.filter(({ field }) => !isCreateDrawerPrimary(field));
    if (lead.length === 0) {
      return { primary: [...named, ...related], extras: [] as Entry[] };
    }
    return { primary: [...lead, ...related], extras: rest };
  }, [row, schema]);

  const subtitle = rowSubtitle(row);

  return (
    <Modal
      open={open}
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      maxWidth="max-w-2xl"
      variant="drawer"
      footer={
        <div className="flex justify-end gap-2">
          {actions}
          <Button
            onClick={onClose}
            variant="secondary" size="sm"
          >
            Close
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {hasHistory && (
          // Two tabs, not a second drawer: the history is about the row that
          // is already open, so it belongs in the same place as its values.
          <div
            role="tablist"
            aria-label="Record views"
            className="flex shrink-0 gap-1 border-b border-od-border"
          >
            {(
              [
                ["details", "Details"],
                ["history", "History"],
              ] as const
            ).map(([value, label]) => (
              <Button
                key={value}
                variant="ghost"
                size="sm"
                role="tab"
                id={`${tabId}-${value}`}
                aria-controls={`${tabId}-${value}-panel`}
                aria-selected={tab === value}
                tabIndex={tab === value ? 0 : -1}
                ref={(node) => { tabRefs.current[value] = node; }}
                onKeyDown={(event) => handleTabKeyDown(event, value)}
                onClick={() => setTab(value)}
                className={`-mb-px rounded-none border-b-2 px-3 text-sm hover:bg-transparent ${
                  tab === value
                    ? "border-od-primary font-medium text-od-text"
                    : "border-transparent text-od-muted hover:text-od-text"
                }`}
              >
                {label}
              </Button>
            ))}
          </div>
        )}

        {hasHistory && (
          <div role="tabpanel" id={`${tabId}-history-panel`} aria-labelledby={`${tabId}-history`} hidden={tab !== "history"} tabIndex={0}>
          {tab === "history" && (
          <RowHistory
            datasourceId={datasourceId!}
            collection={collection!}
            rowKey={rowKey!}
          />
          )}
          </div>
        )}

        <div role={hasHistory ? "tabpanel" : undefined} id={`${tabId}-details-panel`} aria-labelledby={hasHistory ? `${tabId}-details` : undefined} hidden={hasHistory && tab !== "details"} tabIndex={hasHistory ? 0 : undefined}>
        {(!hasHistory || tab === "details") && (
          <div className="flex flex-col gap-3">
        <dl className="m-0 grid grid-cols-1 sm:grid-cols-[minmax(8rem,12rem)_1fr] sm:gap-x-4">
          {primary.map((entry) => (
            <FieldRow key={entry.key} entry={entry} row={row} />
          ))}
        </dl>

        {extras.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <Button
              aria-expanded={showExtras}
              onClick={() => setShowExtras((openExtras) => !openExtras)}
              variant="link" size="sm" className="self-start"
            >
              {showExtras
                ? "Hide extra fields"
                : `Show ${extras.length} extra ${
                    extras.length === 1 ? "field" : "fields"
                  }`}
            </Button>
            {!showExtras && (
              <p className="text-xs text-od-muted">
                {extras.map((entry) =>
                  entry.field ? fieldLabel(entry.field) : entry.key
                ).join(" · ")}
              </p>
            )}
          </div>
        )}

        {showExtras && extras.length > 0 && (
          <dl className="m-0 grid grid-cols-1 sm:grid-cols-[minmax(8rem,12rem)_1fr] sm:gap-x-4">
            {extras.map((entry) => (
              <FieldRow key={entry.key} entry={entry} row={row} />
            ))}
          </dl>
        )}
          </div>
        )}
        </div>
      </div>
    </Modal>
  );
};

export default RowDetailModal;
